import { useEffect, useRef, useState } from "react";
import api, { eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, X, Package, Lightbulb } from "lucide-react";
import { toast } from "sonner";

const NEWT = () => ({ nome: "", descrizione: "", kwp: 0, righe: [] });
const lineTotal = (r) => (+r.quantita || 0) * (+r.prezzo_unitario || 0);

export default function TemplateManager({ open, onOpenChange, onSaved }) {
  const [list, setList] = useState([]);
  const [sugg, setSugg] = useState([]);
  const [sel, setSel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lp, setLp] = useState("");
  const [lpRes, setLpRes] = useState([]);
  const timer = useRef(null);

  const load = () => api.get("/quote-templates").then((r) => setList(r.data.templates || [])).catch(() => {});
  const loadSugg = () => api.get("/quote-templates/suggestions").then((r) => setSugg(r.data.suggestions || [])).catch(() => setSugg([]));
  useEffect(() => { if (open) { load(); loadSugg(); setSel(null); } }, [open]);

  const acceptSugg = async (s) => {
    try {
      await api.post("/quote-templates/suggestions/accept", { nome: s.nome, kwp: s.kwp, righe: s.righe });
      toast.success(`Template "${s.nome}" creato`);
      setSugg((v) => v.filter((x) => x.signature !== s.signature)); load(); onSaved?.();
    } catch { toast.error("Errore creazione template"); }
  };
  const dismissSugg = async (s) => {
    try { await api.post("/quote-templates/suggestions/dismiss", { signature: s.signature }); setSugg((v) => v.filter((x) => x.signature !== s.signature)); }
    catch { toast.error("Errore"); }
  };

  const searchListino = (v) => {
    setLp(v); clearTimeout(timer.current);
    if (v.trim().length < 2) { setLpRes([]); return; }
    timer.current = setTimeout(() => {
      api.get(`/listino?abilitato=1&search=${encodeURIComponent(v)}`).then((r) => setLpRes(r.data.products || [])).catch(() => setLpRes([]));
    }, 300);
  };
  const addRow = (p) => {
    setSel((s) => ({ ...s, righe: [...s.righe, { listino_id: p.id, codice: p.codice, descrizione: p.descrizione, tipologia: p.tipologia, um: p.um || "Pz", quantita: 1, prezzo_unitario: p.prezzo || 0, sconto_pct: 0, kwp: p.kwp || 0, costo_unitario: p.costo || 0 }] }));
    setLp(""); setLpRes([]);
  };
  const setRow = (i, patch) => setSel((s) => ({ ...s, righe: s.righe.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const delRow = (i) => setSel((s) => ({ ...s, righe: s.righe.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!sel.nome.trim()) { toast.error("Indica un nome"); return; }
    setSaving(true);
    const body = { nome: sel.nome, descrizione: sel.descrizione, kwp: +sel.kwp || 0, righe: sel.righe };
    try {
      if (sel.id) await api.put(`/quote-templates/${sel.id}`, body);
      else await api.post("/quote-templates", body);
      toast.success("Template salvato"); setSel(null); load(); onSaved?.();
    } catch { toast.error("Errore salvataggio template"); }
    finally { setSaving(false); }
  };
  const del = async (t) => {
    if (!window.confirm(`Eliminare il template "${t.nome}"?`)) return;
    try { await api.delete(`/quote-templates/${t.id}`); toast.success("Template eliminato"); load(); onSaved?.(); if (sel?.id === t.id) setSel(null); }
    catch { toast.error("Errore eliminazione"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="template-manager">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="h-5 w-5 text-primary" /> Template impianto</DialogTitle></DialogHeader>
        {sugg.length > 0 && (
          <div className="mb-3 space-y-2 rounded-md border-2 border-primary/40 bg-primary/5 p-3" data-testid="template-suggestions">
            <div className="flex items-center gap-1.5 text-sm font-bold"><Lightbulb className="h-4 w-4 text-primary" /> {sugg.length} template suggeriti dalle ricorrenze (anno in corso)</div>
            {sugg.map((s) => (
              <div key={s.signature} className="rounded-md border border-input bg-background p-2" data-testid={`sugg-${s.signature}`}>
                <div className="flex items-center gap-2">
                  <Input value={s.nome} onChange={(e) => setSugg((v) => v.map((x) => (x.signature === s.signature ? { ...x, nome: e.target.value } : x)))} className="h-8 text-sm" data-testid={`sugg-nome-${s.signature}`} />
                  <Badge variant="secondary" className="shrink-0">ricorre {s.count}×</Badge>
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">{s.kwp ? `${s.kwp} kWp · ` : ""}{s.righe.map((r) => `${r.quantita}× ${r.codice}`).join(" · ")}</div>
                <div className="mt-2 flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => dismissSugg(s)} data-testid={`sugg-ignore-${s.signature}`}>Ignora</Button>
                  <Button size="sm" onClick={() => acceptSugg(s)} data-testid={`sugg-create-${s.signature}`}><Plus className="mr-1 h-4 w-4" /> Crea template</Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[260px_1fr]">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Template</span>
              <Button size="sm" variant="outline" onClick={() => setSel(NEWT())} data-testid="template-new"><Plus className="mr-1 h-4 w-4" /> Nuovo</Button>
            </div>
            <div className="space-y-1">
              {list.map((t) => (
                <div key={t.id} className={`flex items-center justify-between rounded-md border border-input p-2 text-sm ${sel?.id === t.id ? "bg-accent" : ""}`} data-testid={`template-item-${t.id}`}>
                  <button type="button" className="flex-1 text-left" onClick={() => setSel({ ...t, righe: (t.righe || []).map((r) => ({ ...r })) })}>
                    <div className="font-semibold">{t.nome}</div>
                    <div className="text-[11px] text-muted-foreground">{t.kwp ? `${t.kwp} kWp · ` : ""}{(t.righe || []).length} righe</div>
                  </button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => del(t)} data-testid={`template-del-${t.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
              {list.length === 0 && <p className="text-xs text-muted-foreground">Nessun template.</p>}
            </div>
          </div>

          <div>
            {!sel ? (
              <div className="flex h-full items-center justify-center py-10 text-center text-sm text-muted-foreground">Seleziona un template o creane uno nuovo.</div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_100px]">
                  <div><Label className="text-xs">Nome</Label><Input value={sel.nome} onChange={(e) => setSel({ ...sel, nome: e.target.value })} data-testid="template-nome" /></div>
                  <div><Label className="text-xs">kWp</Label><Input type="number" step="0.1" value={sel.kwp} onChange={(e) => setSel({ ...sel, kwp: e.target.value })} data-testid="template-kwp" /></div>
                </div>
                <div><Label className="text-xs">Descrizione</Label><Input value={sel.descrizione} onChange={(e) => setSel({ ...sel, descrizione: e.target.value })} data-testid="template-descrizione" /></div>
                <div className="relative">
                  <Label className="text-xs">Aggiungi dal listino</Label>
                  <Input value={lp} onChange={(e) => searchListino(e.target.value)} placeholder="Cerca codice o descrizione…" data-testid="template-listino-search" />
                  {lpRes.length > 0 && (
                    <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-input bg-popover shadow-lg">
                      {lpRes.slice(0, 25).map((p, i) => (
                        <button key={p.id} type="button" onClick={() => addRow(p)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent" data-testid={`template-listino-result-${i}`}>
                          <span className="truncate"><span className="font-mono text-xs font-semibold">{p.codice}</span> — {p.descrizione}</span>
                          <span className="shrink-0 text-xs font-semibold">{p.prezzo == null ? "—" : eur(p.prezzo)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto rounded-md border border-input">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs"><tr><th className="p-2 text-left">Descrizione</th><th className="p-2 text-right w-20">Q.tà</th><th className="p-2 text-right w-28">Prezzo</th><th className="p-2 text-right w-24">Totale</th><th className="p-2 w-8"></th></tr></thead>
                    <tbody>
                      {sel.righe.length === 0 ? (
                        <tr><td colSpan={5} className="p-3 text-center text-muted-foreground">Nessuna riga.</td></tr>
                      ) : sel.righe.map((r, i) => (
                        <tr key={i} className="border-t border-input" data-testid={`template-row-${i}`}>
                          <td className="p-1.5"><Input value={r.descrizione} onChange={(e) => setRow(i, { descrizione: e.target.value })} className="h-8 text-xs" />{(r.codice || r.kwp) && <span className="text-[10px] text-muted-foreground">{r.codice}{r.kwp ? ` · ${r.kwp} kWp` : ""}</span>}</td>
                          <td className="p-1.5"><Input type="number" step="0.01" value={r.quantita} onChange={(e) => setRow(i, { quantita: e.target.value })} className="h-8 text-right text-xs" data-testid={`template-row-${i}-qty`} /></td>
                          <td className="p-1.5"><Input type="number" step="0.01" value={r.prezzo_unitario} onChange={(e) => setRow(i, { prezzo_unitario: e.target.value })} className="h-8 text-right text-xs" /></td>
                          <td className="p-1.5 text-right font-semibold">{eur(lineTotal(r))}</td>
                          <td className="p-1.5 text-center"><button type="button" onClick={() => delRow(i)} className="text-red-400"><X className="h-4 w-4" /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setSel(null)}>Annulla</Button>
                  <Button onClick={save} disabled={saving} data-testid="template-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva template</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
